import type { ErpModule } from '@/api/platform.api';
import type { PageResponse } from '@/types/api';
import type { NotificationResponse } from '@/types/notification';
import { queryKeys } from '@/lib/query';

type ListParams = { page?: number; size?: number; module?: ErpModule };

function patchList(
  data: PageResponse<NotificationResponse> | undefined,
  patch: (items: NotificationResponse[]) => NotificationResponse[],
): PageResponse<NotificationResponse> | undefined {
  if (!data) return data;
  const content = patch(data.content ?? []);
  return {
    ...data,
    content,
    totalElements: Math.max(0, (data.totalElements ?? content.length) - ((data.content?.length ?? 0) - content.length)),
  };
}

export function optimisticMarkRead(
  data: PageResponse<NotificationResponse> | undefined,
  id: number,
): PageResponse<NotificationResponse> | undefined {
  return patchList(data, (items) =>
    items.map((n) => (n.id === id ? { ...n, readFlag: true } : n)),
  );
}

export function optimisticMarkAllRead(
  data: PageResponse<NotificationResponse> | undefined,
): PageResponse<NotificationResponse> | undefined {
  return patchList(data, (items) => items.map((n) => ({ ...n, readFlag: true })));
}

export function optimisticClearRead(
  data: PageResponse<NotificationResponse> | undefined,
): PageResponse<NotificationResponse> | undefined {
  return patchList(data, (items) => items.filter((n) => n.readFlag !== true));
}

export function optimisticClearAll(): PageResponse<NotificationResponse> {
  return {
    content: [],
    page: 0,
    size: 10,
    totalElements: 0,
    totalPages: 0,
    first: true,
    last: true,
  };
}

export function notificationQueryKeys(params: ListParams) {
  const normalized = {
    page: params.page ?? 0,
    size: params.size ?? 10,
    module: params.module,
  };
  return {
    list: queryKeys.notifications.list(normalized),
    unread: queryKeys.notifications.unread(normalized.module),
  };
}
